import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertOctagon, CheckCircle2, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { BentoCard, EmptyState, PageHeader } from '~/components/data';
import { HeroMetric, NewRunModal } from '~/components/domain';
import { Button } from '~/components/primitives';
import { RuntimeStatusPill, StatusBadge } from '~/components/status';
import { fetchIntegrationStatus } from '~/lib/api/integrations';
import { fetchDashboardSummary } from '~/lib/api/runs';
import { TASK_TYPE_LABELS, labelOf } from '~/lib/i18n/labels';
import { useShortcut } from '~/lib/shortcuts/useShortcuts';
import { useAuthStore } from '~/store/auth';
import styles from './_auth.index.module.css';

export const Route = createFileRoute('/_auth/')({
  component: Dashboard,
});

function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [newRunOpen, setNewRunOpen] = useState(false);
  useShortcut('n', () => setNewRunOpen(true), m.route_dashboard_shortcut_new_run());

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: fetchDashboardSummary,
    staleTime: 30_000,
    retry: 0,
  });

  // Onboarding redirect (S5.0) — soft; показываем баннер.
  const onboardingPending = user?.tenant_config?.onboarding_date === null;
  const greetingName = user?.full_name ?? user?.email ?? m.route_dashboard_greeting_fallback();

  // FE-168/180/182/183 Pass 9: severity tier для pending_approvals.
  // >168h (7d) → danger + context-aware CTA, >48h → warn, else default.
  const oldestH = data?.pending_approvals?.oldest_age_hours;
  const pendingCount = data?.pending_approvals?.count ?? 0;
  const approvalsSeverity: 'danger' | 'warn' | undefined =
    typeof oldestH === 'number' && oldestH > 168
      ? 'danger'
      : typeof oldestH === 'number' && oldestH > 48
        ? 'warn'
        : undefined;
  const hasOverdueApprovals = approvalsSeverity === 'danger' && pendingCount > 0;
  const breachDays = typeof oldestH === 'number' ? Math.floor(oldestH / 24) : 0;

  // FE-183 Pass 9: при overdue Hero hierarchy инвертируется — danger Hero #1.
  // Pass 10 design review Mi1: unit consistency — при >48h показываем days
  // (consistent с subline «SLA нарушен: N дн.»), при ≤48h — hours.
  const oldestAgeLabel =
    typeof oldestH === 'number'
      ? oldestH > 48
        ? m.route_dashboard_metric_oldest_age_days({ value: breachDays })
        : m.route_dashboard_metric_oldest_age({ value: Math.round(oldestH) })
      : undefined;

  // FE-176 Pass 51: Dashboard Hero metrics computed внутри STAITER (count из БД).
  // ProvenanceChip атрибутирует source = 'staiter' + fetched_at = now() (или
  // data.generated_at от BE response если будет).
  const provenanceNow: { source: 'staiter'; fetched_at: string } = {
    source: 'staiter',
    fetched_at: new Date().toISOString(),
  };

  const pendingApprovalsHero = (
    <HeroMetric
      key="pending-approvals"
      label={m.route_dashboard_metric_pending_approvals()}
      value={pendingCount}
      provenance={provenanceNow}
      {...(oldestAgeLabel ? { deltaLabel: oldestAgeLabel } : {})}
      {...(approvalsSeverity ? { severity: approvalsSeverity } : {})}
      // FE-181/182 Pass 9: при danger — inline subline (textual breach) + action CTA.
      // Pass 10 design review M1: action color теперь neutral (--text-primary) —
      // 3 danger signals (border+value+action) был слишком aggressive.
      {...(hasOverdueApprovals
        ? {
            subline: m.route_dashboard_sla_breach_days({ days: breachDays }),
            action: {
              label: m.route_dashboard_open_approvals_queue(),
              to: '/approvals',
            },
          }
        : {})}
    />
  );

  const openConflictsHero = (
    <HeroMetric
      key="open-conflicts"
      label={m.route_dashboard_metric_open_conflicts()}
      value={data?.open_conflicts?.count ?? (isLoading ? '…' : 0)}
      provenance={provenanceNow}
      delta={data?.open_conflicts?.delta_24h}
      deltaLabel={m.route_dashboard_metric_delta_24h()}
    />
  );

  // UX Mi3 from Pass 9 review: «Завершено» Hero без timestamp — для morning check
  // нужен context. Computed relative-time subline («2 ч назад» / «вчера»).
  const lastRunFinishedAt = data?.last_run?.finished_at;
  const lastRunRelative = (() => {
    if (!lastRunFinishedAt) return undefined;
    const finishedMs = new Date(lastRunFinishedAt).getTime();
    if (Number.isNaN(finishedMs)) return undefined;
    const diffH = (Date.now() - finishedMs) / 3_600_000;
    if (diffH < 1) return m.route_dashboard_last_run_just_now();
    if (diffH < 24) return m.route_dashboard_last_run_hours_ago({ value: Math.floor(diffH) });
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return m.route_dashboard_last_run_yesterday();
    return m.route_dashboard_last_run_days_ago({ value: diffD });
  })();

  const lastRunHero = data?.last_run?.run_id ? (
    <Link
      key="last-run"
      to="/runs/$runId"
      params={{ runId: data.last_run.run_id }}
      className={styles.lastRunLink}
      aria-label={m.route_dashboard_metric_last_run()}
    >
      <HeroMetric
        label={m.route_dashboard_metric_last_run()}
        value={
          data.last_run.business_status
            ? m.business_status_label({ status: data.last_run.business_status })
            : '—'
        }
        {...(lastRunRelative ? { deltaLabel: lastRunRelative } : {})}
      />
    </Link>
  ) : (
    <HeroMetric key="last-run" label={m.route_dashboard_metric_last_run()} value="—" />
  );

  // FE-183 Pass 9: hierarchy — при danger pending_approvals переставляем его в позицию #1
  // (leftmost) чтобы first-eye attractor совпадал с urgent state per UX review.
  const heroOrder = hasOverdueApprovals
    ? [pendingApprovalsHero, openConflictsHero, lastRunHero]
    : [openConflictsHero, pendingApprovalsHero, lastRunHero];

  return (
    <div className={styles.page}>
      <PageHeader
        title={m.page_title_dashboard()}
        description={user ? `${greetingName}` : undefined}
        actions={
          // FE-180 Pass 9: context-aware Primary CTA. При overdue approvals
          // primary = «Открыть согласования (N)» с danger-tone, «Новый запуск»
          // становится ghost. Daily morning check intent.
          hasOverdueApprovals ? (
            <div className={styles.headerActions}>
              <Link to="/approvals" className={styles.urgentCtaLink}>
                <Button variant="destructive" leadingIcon={<AlertOctagon size={14} />}>
                  {m.route_dashboard_open_approvals_count({ count: pendingCount })}
                </Button>
              </Link>
              <Button
                variant="ghost"
                leadingIcon={<Plus size={14} />}
                onClick={() => setNewRunOpen(true)}
              >
                {m.route_dashboard_btn_new_run()}
              </Button>
            </div>
          ) : (
            <Button leadingIcon={<Plus size={14} />} onClick={() => setNewRunOpen(true)}>
              {m.route_dashboard_btn_new_run()}
            </Button>
          )
        }
      />

      {onboardingPending && (
        <div className={styles.onboardingBanner} role="status">
          <div>
            <strong>{m.route_dashboard_onboarding_banner_title()}</strong>{' '}
            {m.route_dashboard_onboarding_banner_body()}
          </div>
          <Link
            to="/onboarding/$step"
            params={{ step: 'welcome' }}
            className={styles.onboardingLink}
          >
            {m.route_dashboard_onboarding_banner_link()}
          </Link>
        </div>
      )}

      <div
        className={styles.heroRow}
        {...(hasOverdueApprovals ? { 'data-priority': 'overdue' } : {})}
      >
        {heroOrder}
      </div>

      <div className={styles.bentoGrid}>
        <BentoCard
          title={m.route_dashboard_card_recent_runs()}
          actions={
            <Link
              to="/runs"
              className={styles.link}
              aria-label={m.route_dashboard_card_recent_runs_all_aria()}
            >
              {m.route_dashboard_card_recent_runs_all()} <ChevronRight size={12} />
            </Link>
          }
          span={2}
        >
          {!data?.recent_runs?.length ? (
            <EmptyState
              title={m.route_dashboard_empty_title()}
              description={m.route_dashboard_empty_description()}
            />
          ) : (
            <ul className={styles.runList}>
              {data.recent_runs.map((r) => (
                <li key={r.id}>
                  <Link to="/runs/$runId" params={{ runId: r.id }} className={styles.runRow}>
                    <span className={styles.runType}>
                      {labelOf(TASK_TYPE_LABELS, r.task_type as keyof typeof TASK_TYPE_LABELS)}
                    </span>
                    <span className={styles.runPeriod}>
                      {r.period.date_from} → {r.period.date_to}
                    </span>
                    {/* Post-design-review: RuntimeStatusPill is canonical для list rows;
                        StatusBadge показывается ТОЛЬКО когда runtime != completed AND
                        business явно отличается (e.g. needs_review / blocked / escalated).
                        FE-178 Pass 42: NOT to render если runtime+business mean the same thing
                        (e.g. failed+failed, completed+completed) — иначе дубль badges в row. */}
                    <RuntimeStatusPill
                      runtime_status={
                        r.runtime_status as Parameters<
                          typeof RuntimeStatusPill
                        >[0]['runtime_status']
                      }
                    />
                    {(() => {
                      // FE-178: deduplicate badge render. BE drift возвращает
                      // 'completed'/'failed' для обоих fields — нужно скрыть второй.
                      if (!r.business_status) return null;
                      const rs = r.runtime_status;
                      const bs = r.business_status;
                      const SAME = new Set([
                        `completed:completed`,
                        `completed:completed_draft`,
                        `failed:failed`,
                        `cancelled:cancelled`,
                      ]);
                      if (SAME.has(`${rs}:${bs}`)) return null;
                      return <StatusBadge status={bs} />;
                    })()}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </BentoCard>

        {/* FE-88: «Новый запуск» уже в page header — здесь оставляем только
            shortcut-links к inbox-разделам. FE-186 Pass 23: добавлены numeric
            badges (count) per link для at-glance count visibility. */}
        <BentoCard title={m.route_dashboard_card_quick_actions()}>
          <div className={styles.quickActions}>
            <Link to="/conflicts" className={styles.quickLink}>
              <span>{m.route_dashboard_quick_conflicts()}</span>
              {typeof data?.open_conflicts?.count === 'number' && data.open_conflicts.count > 0 && (
                <span className={styles.quickBadge}>{data.open_conflicts.count}</span>
              )}
            </Link>
            <Link to="/anomalies" className={styles.quickLink}>
              <span>{m.route_dashboard_quick_anomalies()}</span>
              {/* FE-NEW-3 (QA 2026-06-04): sum 7d trend как proxy для open count.
                  DashboardSummary schema не содержит `open_anomalies` field, но
                  anomalies_trend_7d пригоден для at-glance count. */}
              {(() => {
                const total = (data?.anomalies_trend_7d ?? []).reduce(
                  (acc, d) => acc + (d.count ?? 0),
                  0,
                );
                return total > 0 ? <span className={styles.quickBadge}>{total}</span> : null;
              })()}
            </Link>
            <Link to="/approvals" className={styles.quickLink}>
              <span>{m.route_approvals_title()}</span>
              {pendingCount > 0 && (
                <span
                  className={styles.quickBadge}
                  data-severity={hasOverdueApprovals ? 'danger' : undefined}
                >
                  {pendingCount}
                </span>
              )}
            </Link>
            <Link to="/evidence" className={styles.quickLink}>
              <span>{m.route_dashboard_quick_evidence()}</span>
            </Link>
          </div>
        </BentoCard>

        {/* FE-89 Sprint N+ batch 3 + FE-185 Pass 9: integrations tile.
            Positive empty state с chip-индикаторами per peer (1С/Bitrix/Adesk)
            вместо отсылки в hidden IntegrationStatusBanner. */}
        <IntegrationsTile />
      </div>

      <NewRunModal open={newRunOpen} onClose={() => setNewRunOpen(false)} />
    </div>
  );
}

/** FE-185 Pass 9: Bento Интеграции — positive state с chip per peer. */
function IntegrationsTile(): JSX.Element {
  const { data } = useQuery({
    queryKey: ['integrations', 'status'],
    queryFn: fetchIntegrationStatus,
    staleTime: 30_000,
    retry: 0,
  });
  const items = data?.items ?? [];

  return (
    <BentoCard title={m.route_dashboard_card_integrations()}>
      {items.length === 0 ? (
        <>
          <p className={styles.muted}>{m.route_dashboard_integrations_hint()}</p>
          <Link to="/admin/integrations" className={styles.quickLink}>
            {m.integration_banner_open_admin_aria()} <ChevronRight size={12} />
          </Link>
        </>
      ) : (
        <>
          <div className={styles.integrationChips}>
            {items.map((it) => (
              <span
                key={it.peer}
                className={styles.integrationChip}
                data-health={it.health}
                aria-label={`${m.integration_peer({ peer: it.peer })} — ${m.integration_health_label({ health: it.health })}`}
              >
                {it.health === 'ok' && (
                  <CheckCircle2 size={12} aria-hidden="true" className={styles.chipIconOk} />
                )}
                <span className={styles.chipPeer}>{m.integration_peer({ peer: it.peer })}</span>
                <span className={styles.chipHealth}>
                  {m.integration_health_label({ health: it.health })}
                </span>
              </span>
            ))}
          </div>
          <Link to="/admin/integrations" className={styles.quickLink}>
            {m.integration_banner_open_admin_aria()} <ChevronRight size={12} />
          </Link>
        </>
      )}
    </BentoCard>
  );
}
